import Hero from '../components/sections/Hero';
import Features from '../components/sections/Features';
import { motion } from 'framer-motion';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <Hero />
      
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Who We Are Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block px-4 py-1 bg-[#F4FAF9] rounded-full text-[#0C7779] text-sm font-medium mb-4 tracking-wider uppercase">
            OUR APPROACH
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[#0F172A]">
            Where Learning Meets <span className="text-[#0C7779]">Real Impact</span>
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto leading-relaxed">
            A community-driven platform connecting students with industry mentors for 
            <span className="font-semibold text-[#0F172A]"> practical, project-based learning</span> that translates directly to career success.
          </p>
          
          {/* Core Values */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 max-w-4xl mx-auto">
            <div className="p-6 bg-white border border-[#E2EFEA] rounded-xl hover:border-[#249E94]/40 transition-all duration-300 hover:shadow-sm">
              <div className="w-12 h-12 bg-[#3BC1A8]/10 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#0C7779]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Hands-On Projects</h3>
              <p className="text-[#64748B]">Work on real-world projects that build your portfolio and skills simultaneously.</p>
            </div>
            
            <div className="p-6 bg-white border border-[#E2EFEA] rounded-xl hover:border-[#249E94]/40 transition-all duration-300 hover:shadow-sm">
              <div className="w-12 h-12 bg-[#3BC1A8]/10 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#0C7779]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Expert Mentorship</h3>
              <p className="text-[#64748B]">Learn directly from professionals currently working in your desired field.</p>
            </div>
            
            <div className="p-6 bg-white border border-[#E2EFEA] rounded-xl hover:border-[#249E94]/40 transition-all duration-300 hover:shadow-sm">
              <div className="w-12 h-12 bg-[#3BC1A8]/10 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#0C7779]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Career Readiness</h3>
              <p className="text-[#64748B]">Build the skills and network needed to transition smoothly into your career.</p>
            </div>
          </div>
        </motion.div>

        {/* Features Section */}
        <Features />
        
        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-20"
        >
          <div className="bg-gradient-to-br from-[#F4FAF9] to-white rounded-2xl p-8 md:p-12 border border-[#E2EFEA]">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">
                Ready to Build Your Future?
              </h3>
              <p className="text-[#64748B] mb-8">
                Join students who are already gaining real-world experience and advancing their careers.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/community" 
                  className="px-8 py-3 bg-[#249E94] text-white rounded-lg font-semibold hover:bg-[#1D867C] transition-colors duration-200 shadow-sm hover:shadow"
                >
                  Join Community
                </a>
                <a 
                  href="/mentors" 
                  className="px-8 py-3 bg-white text-[#0C7779] rounded-lg font-semibold border border-[#E2EFEA] hover:bg-[#F4FAF9] transition-colors duration-200"
                >
                  Explore Mentors
                </a>
              </div>
              
              <p className="mt-6 text-sm text-[#64748B]">
                Free to join • No prerequisites • Start learning immediately
              </p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            <div className="text-center p-4 bg-white border border-[#E2EFEA] rounded-lg">
              <div className="text-2xl font-bold text-[#0C7779]">500+</div>
              <div className="text-[#64748B] text-sm">Active Members</div>
            </div>
            <div className="text-center p-4 bg-white border border-[#E2EFEA] rounded-lg">
              <div className="text-2xl font-bold text-[#0C7779]">50+</div>
              <div className="text-[#64748B] text-sm">Real Projects</div>
            </div>
            <div className="text-center p-4 bg-white border border-[#E2EFEA] rounded-lg">
              <div className="text-2xl font-bold text-[#0C7779]">15+</div>
              <div className="text-[#64748B] text-sm">Expert Mentors</div>
            </div>
            <div className="text-center p-4 bg-white border border-[#E2EFEA] rounded-lg">
              <div className="text-2xl font-bold text-[#0C7779]">92%</div>
              <div className="text-[#64748B] text-sm">Satisfaction Rate</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Home;